import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import {
  getFulfillmentOrdersByOrder,
  getMessagingConsent,
  getSubscriptionUrls,
  logPageClick,
  logPageView,
  updateMessagingConsent,
} from "@redotech/redo-customer-portal-app/api";
import {
  SettingsContext,
  StorefrontClientContext,
} from "@redotech/redo-customer-portal-app/contexts/settings";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { AdLinkTypes } from "@redotech/redo-model/ad-link";
import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import { defaultBrandKit } from "@redotech/redo-model/brand-kit";
import {
  MessagingConsent,
  defaultMessagingConsent,
} from "@redotech/redo-model/messaging-consent";
import { Order } from "@redotech/redo-model/order";
import { ProductFilter } from "@redotech/redo-model/product-filters/product-filters-db-parser";
import { OrderTrackingAd } from "@redotech/redo-model/team";
import {
  ExternalShipmentTracker,
  Trackable,
  TrackableFulfillment,
  TrackableType,
  orderToTrackable,
} from "@redotech/redo-model/trackable";
import { Breadcrumbs } from "@redotech/redo-web/breadcrumb";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { Card } from "@redotech/redo-web/card";
import { Checkbox } from "@redotech/redo-web/checkbox";
import { Flex } from "@redotech/redo-web/flex";
import ImageIcon from "@redotech/redo-web/icon-old/image.svg";
import MobileNotificationIcon from "@redotech/redo-web/icon-old/mobile-notification.svg";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { RedoMarketingPopupFormInstance } from "@redotech/redo-web/marketing/signup-form/popup-form-instance";
import { SectionToolbar } from "@redotech/redo-web/section-toolbar";
import { LineItem } from "@redotech/redo-web/settings-elements/order/order-details";
import { FulfillmentOrder } from "@redotech/redo-web/settings-elements/order/pick-up-config-util";
import { Text } from "@redotech/redo-web/text";
import { TextInput } from "@redotech/redo-web/text-input";
import { orderExceptRedo } from "@redotech/redo-web/utils/general-utils";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront";
import { ok } from "@redotech/util/fp/coerced-result";
import * as classNames from "classnames";
import {
  Dispatch,
  SetStateAction,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OrderContext } from "../../order";
import * as trackingCss from "../order-tracking.module.css";
import * as fulfillmentCss from "./fulfillment.module.css";
import { TrackerCard } from "./tracker-card";
import { UpsellProductsCard } from "./upsell-products-card";

export const Fulfillment = memo(function Fulfillment() {
  // @ts-expect-error: strict-null-checks
  const order = useRequiredContext(OrderContext);
  const settings = useRequiredContext(SettingsContext);
  const storefrontClient = useContext(StorefrontClientContext);
  const subscribeUrlLoad = useLoad(async () => {
    return await getSubscriptionUrls(
      order.shopify.customer.id,
      order?.provider,
    );
  }, []);
  const filteredOrder = useMemo(() => orderExceptRedo(order), [order]);

  if (!subscribeUrlLoad?.value) {
    return (
      <Flex
        dir="column"
        gap="4xl"
        maxW="3xl"
        mx="auto"
        my="none"
        p="xl"
        pt="4xl"
        w="full"
        wrap="wrap"
      >
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  return (
    <FulfillmentDisplay
      editMode={false}
      highlightedElement={null}
      onInsertAfter={() => {}}
      onSelect={() => {}}
      setAdSections={() => {}}
      setModified={() => {}}
      settings={settings}
      storefrontClient={storefrontClient}
      subscribeUrl={subscribeUrlLoad.value.subscribeUrl}
      trackable={orderToTrackable(filteredOrder)}
    />
  );
});

export const EditModeFulfillment = memo(function EditModeFulfillment(props: {
  settings: any;
  onInsertAfter: (index: number) => any;
  upsellText?: string;
  className?: string;
  fullOrder: Order;
  storefrontClient?: ShopifyStorefrontClient;
  setAdSections: Dispatch<SetStateAction<OrderTrackingAd[]>>;
  setModified: Dispatch<SetStateAction<boolean>>;
  getProductFilter?: (
    productFilterId: string,
  ) => Promise<ProductFilter | undefined>;
  highlightedElement: HighlightedElement | null;
  onSelect: (highlightedElement: HighlightedElement) => void;
}) {
  return (
    <FulfillmentDisplay
      {...props}
      editMode
      storefrontClient={props.storefrontClient}
      trackable={orderToTrackable(props.fullOrder)}
    />
  );
});

export type HighlightedElement =
  | { type: "ad"; index: number }
  | { type: "upsell" }
  | { type: "line-items" };

export const FulfillmentDisplay = memo(function FulfillmentDisplay({
  settings,
  trackable,
  editMode,
  onInsertAfter,
  upsellText,
  subscribeUrl,
  className,
  storefrontClient,
  setAdSections,
  setModified,
  getProductFilter,
  highlightedElement,
  onSelect,
}: {
  settings: ReturnAppSettings;
  trackable: Trackable;
  editMode: boolean;
  onInsertAfter: (index: number) => any;
  upsellText?: string;
  subscribeUrl?: string;
  className?: string;
  storefrontClient?: ShopifyStorefrontClient | null;
  setAdSections: Dispatch<SetStateAction<OrderTrackingAd[]>>;
  setModified: Dispatch<SetStateAction<boolean>>;
  getProductFilter?: (
    productFilterId: string,
  ) => Promise<ProductFilter | undefined>;
  highlightedElement: HighlightedElement | null;
  onSelect: (highlightedElement: HighlightedElement) => void;
}) {
  const navigate = useNavigate();

  const fulfillmentOrders = useLoad<FulfillmentOrder[]>(async () => {
    return (
      await getFulfillmentOrdersByOrder(
        trackable.originalOrder.shopify?.id?.toString(),
        trackable.provider,
      )
    ).data;
  }, []);

  const messagingConsentLoad = useLoad(async () => {
    // Get customer ID with fallback to empty string
    const customerId = trackable.originalOrder.shopify.customer?.id || "";
    return await getMessagingConsent(customerId, trackable.provider);
  }, []);

  const messagingConsent =
    messagingConsentLoad.value || defaultMessagingConsent;

  // Add this new state to determine if the signup form should be displayed
  const [shouldDisplaySignupForm, setShouldDisplaySignupForm] = useState(false);

  useEffect(() => {
    const adsVisible =
      settings.orderTracking?.ads?.filter(
        (ad) =>
          !ad.showOnlyNonSubscribed ||
          !messagingConsent.emailMarketingConsent.consented ||
          !messagingConsent.smsMarketingConsent.consented,
      ) || [];
    if (!editMode) {
      void logPageView({
        trackableId: trackable.id,
        trackableType: trackable.type,
        images: adsVisible.map((ad) => ad.imageUrl.toString()),
      });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackable.id, trackable.type]);

  const { fulfillmentIndex }: { fulfillmentIndex?: string } = useParams();

  const selectedFulfillment: TrackableFulfillment | undefined =
    trackable.fulfillments.length !== 0 && fulfillmentIndex !== undefined
      ? trackable.fulfillments[editMode ? 0 : parseInt(fulfillmentIndex)]
      : undefined;

  const handleNavigate = (i: number) => {
    navigate(`/returns-portal/orders/${trackable.id}/track/${i}`);
    window.scrollTo(0, 0);
  };

  const trackersWithDefault: ExternalShipmentTracker[] | [undefined] = (() => {
    if (selectedFulfillment) {
      const trackersForFulfillment: ExternalShipmentTracker[] | undefined =
        selectedFulfillment?.trackers;
      return trackersForFulfillment?.length
        ? trackersForFulfillment
        : [undefined];
    } else if (trackable.fulfillments.length > 0) {
      const allTrackers = trackable.fulfillments.flatMap(
        (fulfillment) => fulfillment.trackers,
      );
      return allTrackers.length ? allTrackers : [undefined];
    }
    return [undefined];
  })();

  const upsellProducts =
    trackable.type === TrackableType.ORDER
      ? settings?.orderTracking?.upsellProducts
      : settings?.returnTracking?.upsellProducts;
  const ads =
    trackable.type === TrackableType.ORDER
      ? settings?.orderTracking?.ads || []
      : settings?.returnTracking?.ads || [];
  const lineItemsSection =
    trackable.type === TrackableType.ORDER
      ? settings?.orderTracking?.lineItemsSection
      : settings?.returnTracking?.lineItemsSection;

  const setSelectedAd = (index: string | number | null | undefined) => {
    if (index === null || index === undefined) {
      return;
    }
    onSelect({ type: "ad", index: parseInt(index.toString()) });
  };

  const rpcClient = useContext(RpcClientContext);

  const customerId = trackable.originalOrder.shopify?.customer?.id;

  return (
    <Flex
      className={className}
      dir="column"
      gap="4xl"
      maxW="3xl"
      mx="auto"
      my="none"
      p="xl"
      pt="4xl"
      wrap="wrap"
    >
      {!editMode && <Breadcrumbs size="small" />}
      <Flex className={trackingCss.mobileColumn} gap="xl">
        <Flex gap="xl">
          <Flex dir="column" gap="xl" w="full">
            <Flex className={trackingCss.mobileColumn} gap="xl">
              <Flex
                className={fulfillmentCss.trackerCardWrapper}
                dir="row"
                gap="xl"
                inert={editMode}
              >
                {trackersWithDefault.map((tracker, i) => (
                  <TrackerCard
                    fulfillment={selectedFulfillment as any}
                    fulfillmentOrders={fulfillmentOrders.value ?? []}
                    key={i}
                    merchantName={settings.merchantName as string}
                    settings={settings}
                    trackable={trackable}
                    tracker={tracker}
                  />
                ))}
              </Flex>
              <Flex dir="column" flex={1.5} gap="xl">
                <UpsellProductsCard
                  editMode={editMode}
                  fulfillment={selectedFulfillment || null}
                  getProductFilter={getProductFilter}
                  onUpsellSectionSelect={() => onSelect({ type: "upsell" })}
                  rpcClient={rpcClient}
                  settings={settings}
                  storefrontClient={storefrontClient}
                  trackable={trackable}
                  upsellSectionHighlighted={
                    highlightedElement?.type === "upsell"
                  }
                  upsellSectionSettings={upsellProducts}
                  upsellText={upsellText}
                />

                {settings?.orderTracking?.billing?.texts?.enabled &&
                  customerId && (
                    <NotificationsCard
                      customerId={customerId}
                      editMode={editMode}
                      messagingConsent={messagingConsent}
                      shouldShowSubscribeCheckbox={!shouldDisplaySignupForm}
                    />
                  )}
              </Flex>
            </Flex>
            <MaybeEditModeWrapper
              className={fulfillmentCss.lineItemsSection}
              editMode={editMode}
              isHighlighted={highlightedElement?.type === "line-items"}
              onClick={() => onSelect({ type: "line-items" })}
            >
              <ItemCards
                editMode={editMode}
                handleNavigate={handleNavigate}
                imageAspectRatio={lineItemsSection?.imageAspectRatio ?? null}
                selectedFulfillment={selectedFulfillment}
                trackable={trackable}
              />
            </MaybeEditModeWrapper>
          </Flex>
        </Flex>
        <Flex dir="column" flex={1} gap="xl" inert={false}>
          {ads.map((ad, index) => (
            <Flex dir="row" gap="sm" key={index} position="relative">
              <AdCard
                ad={{
                  ...ad,
                  ...(editMode
                    ? {
                        onInspectAd: () => setSelectedAd(index),
                        onInsertAd: () => onInsertAfter(index),
                      }
                    : {}),
                }}
                highlighted={
                  highlightedElement?.type === "ad" &&
                  highlightedElement.index === index
                }
                isSubscribed={
                  messagingConsent?.emailMarketingConsent?.consented
                }
                subscribeUrl={subscribeUrl}
                trackable={trackable}
              />
              <SectionToolbar<OrderTrackingAd>
                canDuplicate={false}
                index={index}
                minimumSections={0}
                open={
                  highlightedElement?.type === "ad" &&
                  index === highlightedElement.index
                }
                sections={ads}
                setModified={setModified}
                setSections={setAdSections}
                setSelected={setSelectedAd}
                totalSections={ads.length}
              />
            </Flex>
          ))}
        </Flex>
      </Flex>
      {settings.marketing?.enabled && !editMode && (
        // if we want this to show up in the Merchant Dashboard tracking page settings preview,
        // we'll need to add rpcClient as a context in the Merchant Dashboard.
        <RedoMarketingPopupFormInstance
          brandKit={settings.brandKit ?? defaultBrandKit}
          entryPoint={MarketingEntryPoint.ORDER_TRACKING}
          onShouldDisplayChanged={setShouldDisplaySignupForm}
          rpcClient={rpcClient}
          shopifyCustomerId={
            trackable.originalOrder.shopify.customer?.id.toString() ?? null
          }
          site="tracking-page"
        />
      )}
    </Flex>
  );
});

const MaybeEditModeWrapper = ({
  className,
  editMode,
  onClick,
  isHighlighted,
  children,
}: {
  className?: string;
  editMode: boolean;
  onClick: () => void;
  isHighlighted: boolean;
  children: React.ReactNode;
}) => {
  if (editMode) {
    return (
      <div
        className={classNames(
          className,
          fulfillmentCss.editModeWrapper,
          ...(isHighlighted ? [fulfillmentCss.highlighted] : []),
        )}
        onClick={onClick}
      >
        {children}
      </div>
    );
  }
  return <>{children}</>;
};

const ItemCards = ({
  trackable,
  editMode,
  handleNavigate,
  selectedFulfillment,
  imageAspectRatio,
}: {
  trackable: Trackable;
  editMode: boolean;
  handleNavigate: (i: number) => void;
  selectedFulfillment?: TrackableFulfillment;
  imageAspectRatio: number | null;
}) => {
  if (trackable.type === TrackableType.ORDER) {
    return (
      <Flex dir="column" gap="xl" inert={editMode}>
        {selectedFulfillment !== undefined ? (
          <>
            <Card>
              <Text fontSize="lg" fontWeight="semibold">
                Items in this package
                {(selectedFulfillment.tracking_numbers?.length ?? 0) > 1 && (
                  <Text fontSize="sm">
                    Some items are being shipped in multiple parts
                  </Text>
                )}
              </Text>
              <IterableMap
                items={selectedFulfillment.lineItems}
                keyFn={(item) => item.id}
              >
                {(item, i) => (
                  <LineItem imageAspectRatio={imageAspectRatio} item={item} />
                )}
              </IterableMap>
            </Card>
            <IterableMap
              items={trackable.fulfillments || []}
              keyFn={(f: any) => f.id}
            >
              {(fulfillment: TrackableFulfillment, i: number) =>
                fulfillment.id === selectedFulfillment.id ? (
                  <div />
                ) : (
                  <Card gap="none" key={i}>
                    <Flex justify="space-between">
                      <Text fontSize="lg" fontWeight="semibold">
                        Items in another package
                      </Text>
                      <Button
                        onClick={() => handleNavigate(i)}
                        size={ButtonSize.MICRO}
                        theme={ButtonTheme.OUTLINED}
                      >
                        Track Package
                      </Button>
                    </Flex>
                    <IterableMap
                      items={fulfillment?.lineItems ?? []}
                      keyFn={(item) => item.id}
                    >
                      {(item, i) => (
                        <LineItem
                          imageAspectRatio={imageAspectRatio}
                          item={item}
                        />
                      )}
                    </IterableMap>
                  </Card>
                )
              }
            </IterableMap>
          </>
        ) : (
          <Card>
            <Text fontSize="lg" fontWeight="semibold">
              Items in this order
            </Text>
            <IterableMap
              items={trackable.lineItems ?? []}
              keyFn={(item) => item.id}
            >
              {(item, i) => (
                <LineItem imageAspectRatio={imageAspectRatio} item={item} />
              )}
            </IterableMap>
          </Card>
        )}
      </Flex>
    );
  } else {
    return (
      <Flex dir="column" gap="xl" inert={editMode}>
        <Card>
          <Text fontSize="lg" fontWeight="semibold">
            What you're returning
          </Text>
          <IterableMap
            items={trackable.lineItems || []} // these line items are filtered to only include items that are being returned in trackable.ts
            keyFn={(item) => item.id.toString()}
          >
            {(item, i) => (
              <LineItem imageAspectRatio={imageAspectRatio} item={item} />
            )}
          </IterableMap>
        </Card>
      </Flex>
    );
  }
};

export interface EditAdProps extends OrderTrackingAd {
  onInspectAd(ad: OrderTrackingAd): void;
  onInsertAd(ad: OrderTrackingAd): void;
}

const AdCard = memo(function AdCard({
  trackable,
  ad,
  highlighted,
  subscribeUrl,
  isSubscribed,
}: {
  trackable: Trackable;
  ad: OrderTrackingAd | EditAdProps;
  highlighted?: boolean;
  subscribeUrl?: string;
  isSubscribed?: boolean;
}) {
  if (
    ad.type === AdLinkTypes.SUBSCRIBE &&
    ad.showOnlyNonSubscribed &&
    isSubscribed
  ) {
    return null;
  }

  const classes = classNames(
    fulfillmentCss.adCard,
    ...(ad.imageUrl ? [] : [fulfillmentCss.empty]),
  );

  const borderClasses = classNames(
    fulfillmentCss.adCardBorder,
    ...(highlighted ? [fulfillmentCss.highlighted] : []),
  );

  const isEditMode = "onInspectAd" in ad && "onInsertAd" in ad;

  const card = (
    <Card
      bgImage={ad.imageUrl}
      bgImageAlt={ad.altText}
      className={classes}
      onClick={() => {
        // On merchant dashboard edit order tracking page
        if (isEditMode) {
          return;
        }
        if (trackable.id) {
          void logPageClick({
            trackableId: trackable.id,
            trackableType: trackable.type,
            eventType: "ad",
            url: ad.url,
            image: ad.imageUrl.toString(),
          });
        }
      }}
      onMouseDown={(e?: React.MouseEvent) => e?.stopPropagation()}
    >
      {/* Customizable text and button not yet supported
        <span className={fulfillmentCss.title}>New style dropping weekly</span>
        <Button theme={ButtonTheme.SOLID_LIGHT}>Shop weekly drop</Button>
        */}
      {!ad.imageUrl && (
        <>
          <ImageIcon height={32} width={32} />
          {!ad.imageUrl && "Add image"}
        </>
      )}
    </Card>
  );

  if (isEditMode) {
    return (
      <div className={borderClasses}>
        <div
          onClick={(e) => {
            e.stopPropagation();
            ad.onInspectAd(ad);
          }}
        >
          {card}
        </div>
        <Button
          onClick={() => ad.onInsertAd(ad)}
          size={ButtonSize.MICRO}
          theme={ButtonTheme.BRAND}
        >
          +
        </Button>
      </div>
    );
  }

  // add utm params to url if not subscribing
  const urlResult = ok(ad?.url).map((url) => (url ? new URL(url) : null));
  const urlWithUtm =
    urlResult.ok === true
      ? urlResult.value
      : (console.error("Error parsing ad URL", urlResult.error), null);
  urlWithUtm?.searchParams.append("utm_source", "redo");
  urlWithUtm?.searchParams.append("utm_medium", "order_tracking");

  // not the intended use of utm parameters, but we can use them to track the order >:)
  urlWithUtm?.searchParams.append("utm_content", "ad");
  urlWithUtm?.searchParams.append("utm_term", trackable.id);

  if (ad.type === AdLinkTypes.SUBSCRIBE) {
    return (
      <a
        href={subscribeUrl}
        rel="noreferrer"
        style={{ flexGrow: 1 }}
        target="_blank"
      >
        {card}
      </a>
    );
  } else if (urlWithUtm) {
    return (
      <a
        href={urlWithUtm.toString()}
        rel="noreferrer"
        style={{ flexGrow: 1 }}
        target="_blank"
      >
        {card}
      </a>
    );
  }

  return card;
});

const NotificationsCard = memo(function NotificationsCard({
  messagingConsent,
  customerId,
  editMode,
  shouldShowSubscribeCheckbox,
}: {
  messagingConsent: MessagingConsent;
  customerId: string;
  editMode?: boolean;
  shouldShowSubscribeCheckbox: boolean;
}) {
  const [phoneNumber, setPhoneNumber] = useState(
    messagingConsent?.smsTransactionalConsent.phoneNumber,
  );
  const [marketingConsent, setMarketingConsent] = useState(
    messagingConsent?.smsMarketingConsent.consented,
  );
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState(
    messagingConsent?.smsTransactionalConsent.phoneNumber,
  );
  const [originalMarketingConsent, setOriginalMarketingConsent] = useState(
    messagingConsent?.smsMarketingConsent.consented,
  );
  const [canSave, setCanSave] = useState(false);

  const [saveLoad, doSave] = useTriggerLoad(async () => {
    try {
      setOriginalMarketingConsent(marketingConsent);
      setOriginalPhoneNumber(phoneNumber);
      await updateMessagingConsent(customerId, {
        smsTransactionalConsent: { phoneNumber, consented: true },
        smsMarketingConsent: { consented: marketingConsent, phoneNumber },
      });
    } catch (e) {
      console.error(e);
    }
  });

  useEffect(() => {
    if (
      (phoneNumber !== originalPhoneNumber ||
        marketingConsent !== originalMarketingConsent) &&
      phoneNumber.length > 9
    ) {
      setCanSave(true);
    } else {
      setCanSave(false);
    }
  }, [
    phoneNumber,
    marketingConsent,
    originalMarketingConsent,
    originalPhoneNumber,
  ]);

  return (
    <Card>
      <Flex dir="column" inert={editMode}>
        <Flex>
          <MobileNotificationIcon height={32} width={32} />
          <Text fontSize="md" fontWeight="semibold">
            Get updates about your delivery.
          </Text>
        </Flex>
        <Flex align="center">
          <TextInput
            fullwidth
            onChange={(e) => setPhoneNumber(e)}
            placeholder="Enter your phone number"
            value={phoneNumber}
          />
          <Button
            disabled={!canSave}
            onClick={() => doSave()}
            pending={saveLoad.pending}
            size={ButtonSize.SMALL}
            theme={ButtonTheme.DARK}
          >
            Sign up
          </Button>
        </Flex>
        {shouldShowSubscribeCheckbox && (
          <Flex>
            <Checkbox
              onChange={() => {
                setMarketingConsent(!marketingConsent);
              }}
              value={marketingConsent}
            >
              Text me with news and offers
            </Checkbox>
          </Flex>
        )}
      </Flex>
    </Card>
  );
});
